/**
 * 🐦‍🔥 Rare Unlock Cry — synthesized Web Audio API phoenix cry.
 *
 * Owner directive (Feb 2026): plays on both SpinWheel reveal and
 * CollectionBook pack opening when the rarity is Purple, Orange, or Gold.
 * NO external audio file — must be synthesized live:
 *   • bright, rising
 *   • 2 oscillators + exponential decay
 *
 * Call-site contract: `playRareUnlockCry(rarity)` — no-ops for any
 * rarity not in the trigger set, so callers can fire it unconditionally.
 */

// Rarities that trigger the cry (owner-locked set).
const TRIGGER_RARITIES = new Set(["purple", "orange", "gold"]);

// Alias map so legacy rarity labels (rare/epic/legendary) still fire.
const RARITY_ALIASES = {
  rare:      "purple",
  epic:      "orange",
  legendary: "gold",
  red:       "gold",
  pinnacle:  "gold",
  artifact:  "gold",
  divine:    "orange",
};

// Lazy singleton so we don't spin up multiple AudioContexts.
let _ctx = null;
function getCtx() {
  if (_ctx) return _ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
    return _ctx;
  } catch {
    return null;
  }
}

/**
 * Fire the synthesized cry for a given rarity label.
 *
 * @param {string} rarity - card rarity ("purple" | "orange" | "gold" | aliases)
 * @returns {boolean} true if a cry was played, false otherwise.
 */
export function playRareUnlockCry(rarity) {
  const key = String(rarity || "").trim().toLowerCase();
  const normalized = RARITY_ALIASES[key] || key;
  if (!TRIGGER_RARITIES.has(normalized)) return false;

  const ctx = getCtx();
  if (!ctx) return false;

  // Resume if the browser suspended the context (autoplay policy).
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch { /* best-effort */ }
  }

  const now = ctx.currentTime;

  // Pitch bank — gold is the brightest + highest, purple the lowest.
  const PITCHES = {
    purple: { f1Start: 520, f1End: 1040, f2Start: 780, f2End: 1760, dur: 1.0, peak: 0.22 },
    orange: { f1Start: 620, f1End: 1480, f2Start: 930, f2End: 2480, dur: 1.2, peak: 0.28 },
    gold:   { f1Start: 740, f1End: 1900, f2Start: 1110, f2End: 3200, dur: 1.5, peak: 0.34 },
  };
  const P = PITCHES[normalized];

  // Shared gain envelope — fast attack, exponential decay.
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(P.peak, now + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + P.dur);
  gain.connect(ctx.destination);

  // Oscillator 1 — bright triangle, rising fundamental.
  const osc1 = ctx.createOscillator();
  osc1.type = "triangle";
  osc1.frequency.setValueAtTime(P.f1Start, now);
  osc1.frequency.exponentialRampToValueAtTime(P.f1End, now + P.dur * 0.75);

  // Oscillator 2 — sine shimmer octave-ish above, rising faster.
  const osc2 = ctx.createOscillator();
  osc2.type = "sine";
  osc2.frequency.setValueAtTime(P.f2Start, now);
  osc2.frequency.exponentialRampToValueAtTime(P.f2End, now + P.dur * 0.6);

  // Mix osc2 slightly quieter so osc1 remains the body of the cry.
  const osc2Gain = ctx.createGain();
  osc2Gain.gain.value = 0.55;
  osc2.connect(osc2Gain);
  osc2Gain.connect(gain);
  osc1.connect(gain);

  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + P.dur + 0.05);
  osc2.stop(now + P.dur + 0.05);

  return true;
}

/**
 * Return the brightest trigger rarity found in a list of card drops,
 * or null if none qualify. Priority: gold > orange > purple.
 */
export function pickBrightestRarity(cards) {
  if (!Array.isArray(cards)) return null;
  const order = ["gold", "orange", "purple"];
  const seen = new Set();
  for (const c of cards) {
    const key = String(c?.rarity || "").trim().toLowerCase();
    const normalized = RARITY_ALIASES[key] || key;
    if (TRIGGER_RARITIES.has(normalized)) seen.add(normalized);
  }
  for (const k of order) if (seen.has(k)) return k;
  return null;
}
